import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Row, Col, Card, RadioChangeEvent } from 'antd';
import { useLocation } from 'react-router-dom';
// import styled from 'styled-components';

import { HeaderLinks, IngestionTabType } from '../shared/admin/HeaderLinks';
import EntityRegistry from '../entity/EntityRegistry';
import { AppBreadcrumb } from './AppBreadcrumbs';
import { SearchBar } from './SearchBar';

import { BreadcrumbContextType, useBreadcrumbContext } from './context/BreadcrumbContext';
import { AutoCompleteResultForEntity, EntityType } from '../../types.generated';
import { breadCrumbUtilService } from './utils/breadcrumbsUtilService';
import { useTranslation } from '../../i18n/useTranslation';

// import { useAppConfig } from '../useAppConfig';
// import { ViewSelect } from '../entity/view/select/ViewSelect';

// const ViewSelectContainer = styled.span`
//     margin-right: 14px;
// `;

type Props = {
    initialQuery?: string;
    title?: string;
    placeholderText?: string;
    suggestions?: Array<AutoCompleteResultForEntity>;
    onSearch?: (query: string, type?: EntityType) => void;
    onQueryChange?: (query: string) => void;
    entityRegistry?: EntityRegistry;
    subtext?: string;
    areLinksHidden?: boolean;
    showAnalyticsButton?: boolean;
    ingestionTabType?: IngestionTabType;
    handleIngestionModeChange?: (e: RadioChangeEvent) => void;
    children?: React.ReactNode;
};

/**
 * A header containing a Logo, Search Bar view, & an account management dropdown.
 */
export const SearchHeader = ({
    initialQuery = '',
    title = '',
    placeholderText = '',
    suggestions = [],
    onSearch = () => {},
    onQueryChange = () => {},
    entityRegistry,
    subtext = '',
    areLinksHidden,
    showAnalyticsButton,
    ingestionTabType,
    children,
    handleIngestionModeChange,
}: Props) => {
    // const appConfig = useAppConfig();
    // const viewsEnabled = appConfig.config?.viewsConfig?.enabled;

    const location = useLocation();
    const { t } = useTranslation();

    // Get breadcrumbs based on current route
    const { routeMappings } = useMemo(() => breadCrumbUtilService(), []);
    const { breadcrumbArray, setBreadcrumbArray }: BreadcrumbContextType = useBreadcrumbContext();

    const [breadcrumbs, setBreadcrumbs] = useState(routeMappings[location.pathname]);

    useEffect(() => {
        setBreadcrumbs(routeMappings[location.pathname]);
    }, [location.pathname, routeMappings]);

    useEffect(() => {
        setBreadcrumbArray(Array.isArray(breadcrumbs) ? breadcrumbs : []);
    }, [breadcrumbs, setBreadcrumbArray]);

    // Find matching route mapping for current path
    if (!breadcrumbs) {
        // Check if pathname starts with any of the route patterns
        const matchingPattern = Object.keys(routeMappings).find((pattern: string) =>
            location.pathname.startsWith(`${pattern.replace('*', '')}/`),
        );
        if (matchingPattern) {
            setBreadcrumbs(routeMappings[matchingPattern]);
        }
    }

    return (
        <>
            <Row wrap={false} gutter={16} className="f-page-header">
                <Col flex="auto">
                    <AppBreadcrumb breadcrumbs={breadcrumbArray} />
                    <Typography.Title level={1} className="f-page-heading">
                        {t(title || (breadcrumbArray[breadcrumbArray.length - 1]?.name ?? ''))}
                    </Typography.Title>
                    {subtext && <Typography.Paragraph className="f-page-subheading">{subtext}</Typography.Paragraph>}
                </Col>
                <Col xs={ingestionTabType && 16} flex={!ingestionTabType ? 'none' : undefined}>
                    {/* {viewsEnabled && (
                        <ViewSelectContainer>
                            <ViewSelect />
                        </ViewSelectContainer>
                    )} */}
                    <HeaderLinks
                        showAnalyticsButton={showAnalyticsButton}
                        areLinksHidden={areLinksHidden}
                        ingestionTabType={ingestionTabType}
                        handleIngestionModeChange={handleIngestionModeChange}
                    />
                </Col>
            </Row>
            <div className="f-page-content">
                {entityRegistry && (
                    <Card className="pa-3">
                        <SearchBar
                            combineSiblings
                            showQuickFilters
                            onSearch={onSearch}
                            suggestions={suggestions}
                            initialQuery={initialQuery}
                            onQueryChange={onQueryChange}
                            entityRegistry={entityRegistry}
                            //  viewsEnabled={viewsEnabled}
                            placeholderText={placeholderText}
                            // setIsSearchBarFocused={setIsSearchBarFocused}
                        />
                    </Card>
                )}
                {children}
            </div>
        </>
    );
};
